import { ImportResult, ImportError, CSVTemplate, Product, Supplier, SupplierPrice } from '../types';
import { productService } from './productService';
import { supplierService } from './supplierService';
import { inventoryService } from './inventoryService';
import { chantierService } from './chantierService';

class ImportExportService {
  // Templates CSV
  getCSVTemplates(): CSVTemplate[] {
    return [
      {
        id: 'products',
        name: 'Produits',
        type: 'products',
        headers: ['reference', 'designation', 'barcode', 'categoryId', 'brandId', 'minThreshold', 'defaultLocationId'],
        example: {
          reference: 'VIS-001',
          designation: 'Vis à bois 4x50mm',
          barcode: '1234567890123',
          categoryId: 'cat-1',
          brandId: 'brand-1',
          minThreshold: '50',
          defaultLocationId: 'loc-1'
        },
        description: 'Import des produits avec référence, désignation, catégorie, marque, seuil minimum et emplacement'
      },
      {
        id: 'suppliers',
        name: 'Fournisseurs',
        type: 'suppliers',
        headers: ['name', 'contact', 'email', 'phone', 'address', 'deliveryDelay', 'paymentTerms'],
        example: {
          name: 'Bois & Matériaux SA',
          contact: 'Jean Dupont',
          email: 'contact@boismateriaux.fr',
          phone: '01 23 45 67 89',
          address: '123 Rue de la Forêt, 75001 Paris',
          deliveryDelay: '3',
          paymentTerms: '30 jours fin de mois'
        },
        description: 'Import des fournisseurs avec coordonnées complètes'
      },
      {
        id: 'supplier_prices',
        name: 'Prix fournisseurs',
        type: 'supplier_prices',
        headers: ['supplierId', 'productReference', 'price', 'minQuantity', 'validFrom'],
        example: {
          supplierId: 'sup-1',
          productReference: 'VIS-001',
          price: '0.15',
          minQuantity: '100',
          validFrom: '2024-01-01'
        },
        description: 'Import des prix fournisseurs par référence produit'
      },
      {
        id: 'inventory',
        name: 'Inventaire',
        type: 'inventory',
        headers: ['productReference', 'countedStock', 'comment', 'locationId'],
        example: {
          productReference: 'VIS-001',
          countedStock: '148',
          comment: 'Comptage OK',
          locationId: 'loc-1'
        },
        description: 'Import des comptages d\'inventaire par référence produit'
      },
      {
        id: 'chantiers',
        name: 'Chantiers',
        type: 'chantiers',
        headers: ['code', 'name', 'chefEquipeId', 'status', 'description', 'address', 'startDate', 'endDate'],
        example: {
          code: 'CH-2024-001',
          name: 'Maison Dupont',
          chefEquipeId: 'user-3',
          status: 'en_cours',
          description: 'Construction maison individuelle',
          address: '123 Rue de la Paix, 75001 Paris',
          startDate: '2024-12-01',
          endDate: '2025-03-01'
        },
        description: 'Import des chantiers avec informations complètes'
      }
    ];
  }

  async downloadCSVTemplate(templateId: string): Promise<string> {
    const template = this.getCSVTemplates().find(t => t.id === templateId);
    if (!template) throw new Error('Template non trouvé');

    const headers = template.headers.join(';');
    const example = template.headers.map(header => template.example[header] || '').join(';');
    
    const csvContent = `${headers}\n${example}`;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    return url;
  }

  // Import CSV
  async importProducts(csvContent: string, userId: string): Promise<ImportResult> {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const lines = csvContent.split('\n').filter(line => line.trim());
    const headers = lines[0].split(';').map(h => h.trim());
    const dataLines = lines.slice(1);
    
    const result: ImportResult = {
      success: 0,
      errors: 0,
      warnings: 0,
      details: []
    };

    for (let i = 0; i < dataLines.length; i++) {
      const lineNumber = i + 2; // +2 car on commence à la ligne 2 (après headers)
      const values = dataLines[i].split(';').map(v => v.trim());
      
      try {
        const productData: any = {};
        headers.forEach((header, index) => {
          productData[header] = values[index] || '';
        });

        // Validation des champs obligatoires
        if (!productData.reference) {
          result.details.push({
            line: lineNumber,
            field: 'reference',
            message: 'Référence obligatoire',
            type: 'error'
          });
          result.errors++;
          continue;
        }

        if (!productData.designation) {
          result.details.push({
            line: lineNumber,
            field: 'designation',
            message: 'Désignation obligatoire',
            type: 'error'
          });
          result.errors++;
          continue;
        }

        // Créer le produit
        await productService.createProduct({
          reference: productData.reference,
          designation: productData.designation,
          barcode: productData.barcode,
          categoryId: productData.categoryId || 'cat-1',
          brandId: productData.brandId || 'brand-1',
          currentStock: 0,
          reservedStock: 0,
          minThreshold: parseInt(productData.minThreshold) || 10,
          defaultLocationId: productData.defaultLocationId || 'loc-1',
          isActive: true
        });

        result.success++;
      } catch (error) {
        result.details.push({
          line: lineNumber,
          message: error instanceof Error ? error.message : 'Erreur inconnue',
          type: 'error'
        });
        result.errors++;
      }
    }

    return result;
  }

  async importSuppliers(csvContent: string, userId: string): Promise<ImportResult> {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const lines = csvContent.split('\n').filter(line => line.trim());
    const headers = lines[0].split(';').map(h => h.trim());
    const dataLines = lines.slice(1);
    
    const result: ImportResult = {
      success: 0,
      errors: 0,
      warnings: 0,
      details: []
    };

    for (let i = 0; i < dataLines.length; i++) {
      const lineNumber = i + 2;
      const values = dataLines[i].split(';').map(v => v.trim());
      
      try {
        const supplierData: any = {};
        headers.forEach((header, index) => {
          supplierData[header] = values[index] || '';
        });

        if (!supplierData.name) {
          result.details.push({
            line: lineNumber,
            field: 'name',
            message: 'Nom du fournisseur obligatoire',
            type: 'error'
          });
          result.errors++;
          continue;
        }

        await supplierService.createSupplier({
          name: supplierData.name,
          contact: supplierData.contact || '',
          email: supplierData.email,
          phone: supplierData.phone,
          address: supplierData.address,
          deliveryDelay: parseInt(supplierData.deliveryDelay) || 7,
          paymentTerms: supplierData.paymentTerms,
          isActive: true
        });

        result.success++;
      } catch (error) {
        result.details.push({
          line: lineNumber,
          message: error instanceof Error ? error.message : 'Erreur inconnue',
          type: 'error'
        });
        result.errors++;
      }
    }

    return result;
  }

  async importInventory(csvContent: string, sessionId: string, userId: string): Promise<ImportResult> {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const lines = csvContent.split('\n').filter(line => line.trim());
    const headers = lines[0].split(';').map(h => h.trim());
    const dataLines = lines.slice(1);
    
    const result: ImportResult = {
      success: 0,
      errors: 0,
      warnings: 0,
      details: []
    };

    for (let i = 0; i < dataLines.length; i++) {
      const lineNumber = i + 2;
      const values = dataLines[i].split(';').map(v => v.trim());
      
      try {
        const inventoryData: any = {};
        headers.forEach((header, index) => {
          inventoryData[header] = values[index] || '';
        });

        if (!inventoryData.productReference) {
          result.details.push({
            line: lineNumber,
            field: 'productReference',
            message: 'Référence produit obligatoire',
            type: 'error'
          });
          result.errors++;
          continue;
        }

        const countedStock = parseInt(inventoryData.countedStock);
        if (isNaN(countedStock)) {
          result.details.push({
            line: lineNumber,
            field: 'countedStock',
            message: 'Stock compté doit être un nombre',
            type: 'error'
          });
          result.errors++;
          continue;
        }

        // Simuler la mise à jour de l'inventaire
        // En réalité, on rechercherait le produit par référence et mettrait à jour l'item d'inventaire
        result.success++;
      } catch (error) {
        result.details.push({
          line: lineNumber,
          message: error instanceof Error ? error.message : 'Erreur inconnue',
          type: 'error'
        });
        result.errors++;
      }
    }

    return result;
  }

  // Export CSV
  async exportProducts(): Promise<string> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const products = await productService.getProducts();
    const categories = await productService.getCategories();
    const brands = await productService.getBrands();
    const locations = await productService.getLocations();
    
    const data = products.map(product => ({
      'Référence': product.reference,
      'Désignation': product.designation,
      'Code-barres': product.barcode || '',
      'Catégorie': productService.getCategoryName(product.categoryId),
      'Marque': productService.getBrandName(product.brandId),
      'Stock actuel': product.currentStock,
      'Stock réservé': product.reservedStock,
      'Stock disponible': productService.getAvailableStock(product),
      'Seuil minimum': product.minThreshold,
      'Emplacement': productService.getLocationName(product.defaultLocationId),
      'Statut': product.isActive ? 'Actif' : 'Inactif',
      'Créé le': new Date(product.createdAt).toLocaleDateString('fr-FR'),
      'Modifié le': new Date(product.updatedAt).toLocaleDateString('fr-FR')
    }));

    return this.generateCSV(data);
  }

  async exportStockMovements(filters?: {
    startDate?: string;
    endDate?: string;
    productId?: string;
    type?: string;
  }): Promise<string> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const movements = await inventoryService.getStockActivity(filters);
    
    const data = movements.map(movement => ({
      'Date': new Date(movement.createdAt).toLocaleString('fr-FR'),
      'Type': inventoryService.getMovementTypeLabel(movement.type),
      'Produit': movement.productId,
      'Quantité': movement.quantity,
      'Emplacement origine': movement.fromLocationId || '',
      'Emplacement destination': movement.toLocationId || '',
      'Raison': movement.reason,
      'Utilisateur': movement.userId
    }));

    return this.generateCSV(data);
  }

  async exportSuppliers(): Promise<string> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const suppliers = await supplierService.getSuppliers();
    
    const data = suppliers.map(supplier => ({
      'Nom': supplier.name,
      'Contact': supplier.contact,
      'Email': supplier.email || '',
      'Téléphone': supplier.phone || '',
      'Adresse': supplier.address || '',
      'Délai livraison (jours)': supplier.deliveryDelay,
      'Conditions paiement': supplier.paymentTerms || '',
      'Statut': supplier.isActive ? 'Actif' : 'Inactif',
      'Créé le': new Date(supplier.createdAt).toLocaleDateString('fr-FR')
    }));

    return this.generateCSV(data);
  }

  async exportChantiers(): Promise<string> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const chantiers = await chantierService.getChantiers();
    
    const data = chantiers.map(chantier => ({
      'Code': chantier.code,
      'Nom': chantier.name,
      'Chef équipe': chantier.chefEquipeId,
      'Statut': chantierService.getStatusLabel(chantier.status),
      'Description': chantier.description || '',
      'Adresse': chantier.address || '',
      'Date début': new Date(chantier.startDate).toLocaleDateString('fr-FR'),
      'Date fin': chantier.endDate ? new Date(chantier.endDate).toLocaleDateString('fr-FR') : '',
      'Créé le': new Date(chantier.createdAt).toLocaleDateString('fr-FR')
    }));

    return this.generateCSV(data);
  }

  private generateCSV(data: Record<string, any>[]): string {
    if (!data.length) return '';
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(';'),
      ...data.map(row => headers.map(header => {
        const value = row[header];
        // Échapper les valeurs contenant des points-virgules ou des guillemets
        if (typeof value === 'string' && (value.includes(';') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(';'))
    ].join('\n');
    
    return csvContent;
  }

  async downloadCSV(csvContent: string, filename: string): Promise<void> {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

export const importExportService = new ImportExportService();